import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import cssModules from 'react-css-modules'
import styles from './crowdfunding_share.less'
import { getI18n } from 'client/i18n'
import StandardError from 'components/common/standard_error'
import CrossFadeTransition from 'components/common/transitions/cross_fade'
import {
  CIP_ELIGIBILITY_CODES,
  COUNTRY_NOT_SUPPORTED,
  QUALTRICS_GN_CREATION_ACTIVE_FEEDBACKLINK,
  QUALTRICS_GN_CREATION_FEEDBACKLINK,
  QUALTRICS_GNC_CREATION_ACTIVE_FEEDBACKLINK,
  QUALTRICS_GNC_CREATION_FEEDBACKLINK,
  UNDER_REVIEW_ERROR,
  POOL_TYPE,
} from 'shared/shared_consts'
import { NEW_CAMPAIGN_ID } from 'redux/modules/campaign'
import { sendEnsightenEvent } from 'lib/ensighten_analytics'
import { getDexterReturnUrl } from 'lib/create_campaign_eligibility'
import ShareCampaignTextarea from 'components/common/share_campaign_textarea'
import { BodyText, Button, HeadingText } from '@paypalcorp/pp-react'
import CampaignEligibility from 'components/common/campaign_eligibility'
import { dexterURL } from 'shared/campaign_url'
import { trackImpression, trackEventNameClick } from 'lib/fpti_analytics'
import FeedbackLink from 'components/feedback_link'
import Pictograms from 'components/common/pictograms'
import HalfSheet from '../../../common/half_sheet/half_sheet'
import { Message } from '@paypalcorp/worldready-react'

export const I18N_CONTEXT = 'components/pages/crowdfunding/pool_share'
const i18n = getI18n(I18N_CONTEXT)
class CrowdfundingShare extends React.Component {
  static propTypes = {
    onPublish: PropTypes.func.isRequired,
    operatingRemotely: PropTypes.bool,
    campaignId: PropTypes.string,
    poolUrl: PropTypes.string.isRequired,
    creationError: PropTypes.object,
    shareDescription: PropTypes.string,
    params: PropTypes.object,
    charity: PropTypes.object,
    paypalDomain: PropTypes.string,
    route: PropTypes.object,
    enable_ces_link: PropTypes.bool,
    cdnHost: PropTypes.string,
    campaignType: PropTypes.string,
  }

  state = {
    showMobileSharePage: false,
  }

  campaignCreated = ({ campaignId }) => {
    return campaignId !== NEW_CAMPAIGN_ID
  }

  componentDidMount() {
    const { operatingRemotely, creationError } = this.props

    // Checking if the campaign has already been created so that we don't create it twice.
    // This can happen when going back from the share page to here.
    if (
      !operatingRemotely &&
      !creationError &&
      !this.campaignCreated(this.props)
    ) {
      this.props.onPublish()
    }
  }

  UNSAFE_componentWillReceiveProps = newProps => {
    if (this.campaignCreated(newProps) && !this.campaignCreated(this.props)) {
      sendEnsightenEvent('MoneyPoolFin')
    }
  }

  renderShareComponent = () => (
    <div styleName="textarea-wrapper">
      <ShareCampaignTextarea
        campaignId={this.props.campaignId}
        description={this.props.shareDescription}
        data-pa-click="share"
      />
    </div>
  )

  renderInfo = () => {
    return this.props.campaignType === POOL_TYPE.PRIVATE ? (
      <BodyText styleName="subtitle">
        <Message id="flow.private.publish.description" />
      </BodyText>
    ) : (
      <>
        <BodyText styleName="subtitle">
          <Message id="components.pages.crowdfunding.pool_share.share_your_page_new" />
        </BodyText>
        <ul styleName="mb-24">
          {[
            'share_your_page_more_1',
            'share_your_page_more_2',
            'share_your_page_more_3',
          ].map(key => {
            return (
              <li key={key}>
                <BodyText>
                  <Message
                    id={`components.pages.crowdfunding.pool_share.${key}`}
                  />
                </BodyText>
              </li>
            )
          })}
        </ul>
      </>
    )
  }

  render() {
    let Component
    const {
      creationError,
      charity,
      enable_ces_link,
      paypalDomain,
      route,
    } = this.props
    const hideLogo = get(this.props, 'params.hideLogo', false)
    const hideButton = get(this.props, 'params.hideButton', false)
    const isPublishPath = route && route.path === 'publish'
    const isShowingCESLink = isPublishPath && enable_ces_link

    if (creationError) {
      if (!CIP_ELIGIBILITY_CODES.hasOwnProperty(creationError.error)) {
        trackImpression({
          error_desc: creationError.error || 'CREATE_ERROR',
          error_code: 500,
          event_type: 'ac',
          event_name: 'dw_giving_failed_customer_interaction_occurred',
          screen: 'publish',
        })
      }

      if (creationError.error === UNDER_REVIEW_ERROR) {
        Component = (
          <StandardError
            key="review_error"
            iconType="error"
            title={i18n('review_error_title')}
            subtitle={i18n('review_error_subtitle')}
            backButton={true}
          />
        )
      } else if (CIP_ELIGIBILITY_CODES.hasOwnProperty(creationError.error)) {
        if (CIP_ELIGIBILITY_CODES[creationError.error] === 'NEED_MORE_DATA') {
          const returnUrl = getDexterReturnUrl(
            '/pools/c/create-crowdfunding/basics'
          )
          location.href = dexterURL(returnUrl)
          return
        } else {
          Component = (
            <CampaignEligibility
              CIPStatusCode={CIP_ELIGIBILITY_CODES[creationError.error]}
            />
          )
        }
      } else if (creationError.error === COUNTRY_NOT_SUPPORTED) {
        Component = (
          <StandardError
            key="error"
            iconType="warning"
            title={i18n('error_country_eligible_title')}
            subtitle={i18n('error_country_eligible_sub_title')}
            extraContent={
              <Button
                as="a"
                href={`https://${paypalDomain}/myaccount/summary`}
                role="button"
                secondary
                styleName="go-to-pool-button"
              >
                <Message id="components.pages.crowdfunding.pool_share.error_country_eligible_button" />
              </Button>
            }
          />
        )
      } else {
        Component = (
          <StandardError
            key="error"
            iconType="warning"
            title={i18n('error_title')}
            subtitle={i18n('error_subtitle')}
            onTryAgain={this.props.onPublish}
          />
        )
      }
    } else if (this.campaignCreated(this.props)) {
      if (isPublishPath) {
        trackImpression({
          event_name: 'dw_giving_fundraiser_publish_screen_shown',
        })
      }

      Component = (
        <div styleName="publish-container">
          {!hideLogo && (
            <div styleName="img-container-header">
              <Pictograms
                classname="creation-share-image"
                svg="heart-grow"
                data-testid="crowdfunding-share-image-url"
              />
            </div>
          )}

          {this.state.showMobileSharePage && (
            <HalfSheet
              show={true}
              title={i18n('share_text')}
              mobileFixedHeight="dynamic"
              onClose={() => this.setState({ showMobileSharePage: false })}
              containerClassName={styles['mshare-container']}
            >
              {this.renderShareComponent()}
            </HalfSheet>
          )}

          <HeadingText size="sm" styleName="title">
            <Message id="components.pages.crowdfunding.pool_share.share_title" />
          </HeadingText>
          {this.renderInfo()}
          <div className="hidden-xs">{this.renderShareComponent()}</div>
          <Button
            className="visible-xs"
            styleName="mb-16"
            onClick={() => this.setState({ showMobileSharePage: true })}
          >
            <Message id="components.pages.crowdfunding.pool_share.share_text" />
          </Button>
          {!hideButton && (
            <Button
              as="a"
              secondary
              data-pa-click="goToPool"
              data-nemo="go-to-pool"
              data-testid="share-go-to-pool"
              href={this.props.poolUrl}
              styleName="go-to-pool-button"
              onClick={() =>
                trackEventNameClick({
                  event_name: 'dw_giving_fundraiser_publish_view_pressed',
                })
              }
            >
              <Message id="components.pages.crowdfunding.pool_share.see_my_story" />
            </Button>
          )}
          {isShowingCESLink && (
            <FeedbackLink
              activeFeedbackName={
                charity && charity.nonprofit_id
                  ? QUALTRICS_GNC_CREATION_ACTIVE_FEEDBACKLINK
                  : QUALTRICS_GN_CREATION_ACTIVE_FEEDBACKLINK
              }
              // eslint-disable-next-line no-undef
              reloadQualtrics={PAYPAL.analytics.reloadQualtrics}
              uniqueFeedbackName={
                charity && charity.nonprofit_id
                  ? QUALTRICS_GNC_CREATION_FEEDBACKLINK
                  : QUALTRICS_GN_CREATION_FEEDBACKLINK
              }
            />
          )}
        </div>
      )
    } else {
      // assume operatingRemotely
      trackImpression({
        pageName: 'create:spinner',
      })
      Component = (
        <div styleName="create-indicator-container" key="spinner">
          <div className="vx_spinner-large" styleName="create-indicator" />
          <div styleName="spinner-title">
            <Message id="components.pages.crowdfunding.pool_share.creating" />
          </div>
        </div>
      )
    }

    return <CrossFadeTransition>{Component}</CrossFadeTransition>
  }
}

export default cssModules(CrowdfundingShare, styles, { allowMultiple: true })
